import React from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { ModuleCard } from '@/components/ui/module-card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  getCachedModuleCatalog,
  loadModuleCatalog,
  type ModuleCatalogCategory
} from '@/components/features/spaces/moduleCatalog'
import { Skeleton } from '@/components/ui/skeleton'
import {
  getModule,
  hasPosting,
  isReadyModule,
  isSingleItemConfig,
  pickerIcon,
  moduleIconName,
  MODULE_NAMESPACES,
  readyModules
} from '@/modules/registry'
import { CommonSpaceFields } from '@/components/features/spaces/wizard/CommonSpaceFields'
import { SpaceLayoutField } from '@/components/features/spaces/SpaceLayoutField'
import { moduleLayoutsOf } from '@/components/features/spaces/moduleCatalog'
import { offeredLayouts } from '@/lib/spaceLayout'
import type { SpaceLayout } from '@/types/spaceLayout'
import { Call4PapersModuleFields } from './wizard/modules/Call4PapersModuleFields'
import { CallSetupForm, emptyCallDraft, validateCallDraft } from '@/components/features/call4papers/CallSetupForm'
import { saveCall } from '@/components/features/call4papers/callPersistence'
import { UrlModuleFields } from '@/components/features/spaces/wizard/modules/UrlModuleFields'
import { CertificatesModuleFields } from '@/components/features/spaces/wizard/modules/CertificatesModuleFields'
import { CoursesModuleFields } from '@/components/features/spaces/wizard/modules/CoursesModuleFields'
import { FeedModuleFields, type FeedScope } from '@/components/features/spaces/wizard/modules/FeedModuleFields'
import { CoursesSingleStep, emptyCourseDraft } from '@/components/features/spaces/wizard/modules/CoursesSingleStep'
import { SurveySingleStep } from '@/components/features/surveys/SurveySingleStep'
import { emptySurveyDraft, type SurveyDraft } from '@/components/features/surveys/surveysSpaceConfig'
import { FormSingleStep } from '@/components/features/forms/FormSingleStep'
import { emptyFormDraft, type FormDraft } from '@/components/features/forms/formsSpaceConfig'
import { CycleSingleStep } from '@/components/features/performanceReview/CycleSingleStep'
import { GoalCycleSingleStep } from '@/components/features/goals/GoalCycleSingleStep'
import { emptyGoalCycle, validateGoalCycle, type GoalCycleDraft } from '@/components/features/goals/goalsUtils'
import { cycleBasicsApiErrors, emptyCycleBasics, validateCycleBasics, type CycleBasicsDraft, type CycleBasicsErrors } from '@/components/features/performanceReview/reviewUtils'
import { DownloadSingleStep } from '@/components/features/downloads/DownloadSingleStep'
import {
  createDownloadSingle,
  downloadSingleFailure,
  emptyDownloadSingleDraft,
  validateDownloadSingle,
  type DownloadSingleDraft,
  type DownloadSingleErrors,
  type DownloadSingleProgress
} from '@/components/features/downloads/downloadSingle'
import type { UploadProgress } from '@/components/features/downloads/downloadUpload'
import { ShowFields } from '@/modules/podcast/engine/ShowFields'
import { createShowFromDraft, emptyShowDraft, type ShowDraft } from '@/modules/podcast/engine/showDraft'
import { StorefrontModuleFields } from '@/components/features/spaces/wizard/modules/StorefrontModuleFields'
import { StorefrontSettingsDialog } from '@/components/features/storefront/StorefrontSettingsDialog'
import { EditSpaceDialog } from '@/components/features/spaces/EditSpaceDialog'
import { Plus, AlertTriangle, Lock } from 'lucide-react'
import { sdk } from '@/lib/sdk'
import { useRoute } from '@/lib/RouteContext'
import { useCommunity } from '@/lib/CommunityContext'
import { generateSlug, slugErrorFrom } from '@/lib/slug'
import { useTranslation } from 'react-i18next'
import { type Section } from '@/types/sections'
import { visibilityRank } from '@/types/access'
import type { ModuleType, CommonSpaceData, ModuleConfig } from '@/types/space-wizard'
import type { StorefrontConfig } from '@/types/storefront'
import { DEFAULT_FEED_WIDGETS, type FeedWidgets, type Space, type SpaceModule } from '@/types/spaces'
import { useAuth } from '@/lib/AuthContext'

/**
 * A space that exists but whose module setup, saved by a second request after
 * `POST /spaces`, did not go through. There is no transaction across the two,
 * so the dialog stops offering "create" (a second try would hit "slug already
 * exists") and offers to finish the setup on the space that is there.
 */
type PendingSetup =
  | { spaceId: string; kind: 'storefront'; selection: StorefrontConfig }
  /* call4papers, certificates, or a module whose setup is saved by its own endpoint. */
  | { spaceId: string; kind: Exclude<SpaceModule, 'storefront'> }

interface CreateSpaceDialogProps {
  section: Section
  onSpaceCreated?: () => void
  trigger?: 'button' | 'icon'
  /** Its own opener instead of the two built-in ones (F-181: "Criar espaço de eventos" in the Feed). */
  triggerElement?: React.ReactElement
  /** A module already picked when the wizard opens (the person can still change it). */
  initialModule?: string
}

/**
 * What the picker shows when `GET /spaces/modules` fails or returns nothing
 * usable: the community modules, as before the catalog existed. Creating a
 * space must never depend on that request.
 */
const FALLBACK_CATALOG: ModuleCatalogCategory[] = [
  {
    key: 'community',
    modules: readyModules().map(({ key }) => ({ key, available: true }))
  }
]

/**
 * Every module the API catalog returns is drawn, in its order and category.
 * Whether it can be picked is decided at the card: only a registered module
 * that is `ready` and that the API marks available. The rest (announced, or
 * registered but not shipped) shows "coming soon". A category goes away only
 * when the API sends it empty; if nothing creatable is in the catalog, the
 * fallback takes over.
 */
const toRenderableCatalog = (categories: ModuleCatalogCategory[] | null): ModuleCatalogCategory[] => {
  if (!categories) return FALLBACK_CATALOG

  const renderable = categories.filter((category) => category.modules.length > 0)
  const hasCreatable = renderable.some((c) => c.modules.some((m) => m.available && isReadyModule(m.key)))
  return hasCreatable ? renderable : FALLBACK_CATALOG
}

/** An announced module with no translation yet reads `newThing` as "New thing", not as a raw i18n key. */
const humanizeKey = (key: string) => key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase())

export const CreateSpaceDialog = ({ section, onSpaceCreated, trigger = 'icon', triggerElement, initialModule }: CreateSpaceDialogProps) => {
  const { t, i18n } = useTranslation(['common', 'navigation', 'entitlements', ...MODULE_NAMESPACES])
  const { community } = useCommunity()
  const { navigate } = useRoute()
  const [open, setOpen] = React.useState(false)
  const [currentStep, setCurrentStep] = React.useState(1)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [error, setError] = React.useState('')
  const [slugError, setSlugError] = React.useState<string | null>(null)

  const [selectedModule, setSelectedModule] = React.useState<ModuleType | null>(null)
  /* F-198 "Exibição": kept with the module it was picked for, so changing module starts on the new default. */
  const [layoutPick, setLayoutPick] = React.useState<{ module: string; layout: SpaceLayout } | null>(null)
  const [commonData, setCommonData] = React.useState<CommonSpaceData>({
    title: '',
    description: '',
    slug: '',
    icon: 'file-text',
    /* A space may never be more open than its section, and the server refuses
       it — so the default starts at the section's own level rather than at a
       value the first save would bounce. */
    visibility: visibilityRank(section.visibility) > visibilityRank('members')
      ? section.visibility
      : 'members'
  })
  const [moduleConfig, setModuleConfig] = React.useState<ModuleConfig>({})
  /* F-162: `null` until the person picks one, and then nothing is sent and the
     server applies its own default. `feedScopeDefault` mirrors that default:
     the only Feed of the section aggregates it, a second one shows its own posts. */
  const [feedScope, setFeedScope] = React.useState<FeedScope | null>(null)
  const [feedScopeDefault, setFeedScopeDefault] = React.useState<FeedScope>('section')
  /* F-181: the side column's widgets, `null` until the person flips one (then only that is sent). */
  const { hasRole } = useAuth()
  const canEditFeedWidgets = hasRole(['owner', 'admin'])
  const [feedWidgets, setFeedWidgets] = React.useState<FeedWidgets | null>(null)
  /* `null` while the catalog is loading: the picker shows a skeleton instead of
     a provisional list, so the cards never jump between layouts. A catalog
     already fetched this session is used straight away. */
  const [catalog, setCatalog] = React.useState<ModuleCatalogCategory[] | null>(() => {
    const cached = getCachedModuleCatalog(sdk.getCommunityId())
    return cached === undefined ? null : toRenderableCatalog(cached)
  })
  const [activeCategory, setActiveCategory] = React.useState(catalog?.[0]?.key ?? '')
  /* Started as the store's retry (F-29); every setup saved after the space
     exists goes through it now (F-64). */
  const [pendingSetup, setPendingSetup] = React.useState<PendingSetup | null>(null)
  const [storefrontPickerOpen, setStorefrontPickerOpen] = React.useState(false)
  /* The space itself, fetched when the owner asks to finish the setup in the
     edit dialog, which needs the whole record. */
  const [pendingSpace, setPendingSpace] = React.useState<Space | null>(null)
  const [retrying, setRetrying] = React.useState(false)
  const [retryError, setRetryError] = React.useState('')
  const errorRef = React.useRef<HTMLDivElement>(null)
  const [courseErrors, setCourseErrors] = React.useState<{ title?: string; level?: string; templateId?: string; text?: string }>({})
  /* Field errors of the module's own setup (`ModuleSetup`), whichever module it is. */
  const [setupErrors, setSetupErrors] = React.useState<unknown>(null)
  /* The survey a one-survey space is born with (F-103), created right after the space. */
  const [surveyDraft, setSurveyDraft] = React.useState<SurveyDraft>(emptySurveyDraft)
  const [surveyTitleError, setSurveyTitleError] = React.useState<string | undefined>()
  /* The space exists but its survey was refused: the step stays, with the input, and only the survey is retried. */
  const [surveySpace, setSurveySpace] = React.useState<{ id: string; slug: string } | null>(null)
  /* The form a one-form space is born with (F-109), created right after the space; refused, only the form is retried. */
  const [formDraft, setFormDraft] = React.useState<FormDraft>(emptyFormDraft)
  const [formTitleError, setFormTitleError] = React.useState<string | undefined>()
  const [formSpace, setFormSpace] = React.useState<{ id: string; slug: string } | null>(null)
  /* The cycle a one-cycle Performance Review space is born with (F-48), created right after the space; refused, only the cycle is retried. */
  const [reviewDraft, setReviewDraft] = React.useState<CycleBasicsDraft>(() => emptyCycleBasics(undefined, ''))
  const [reviewErrors, setReviewErrors] = React.useState<CycleBasicsErrors | null>(null)
  const [reviewSpace, setReviewSpace] = React.useState<{ id: string; slug: string } | null>(null)
  /* The cycle a one-cycle Metas & OKRs space is born with (F-50); refused, only the cycle is retried. */
  const [goalDraft, setGoalDraft] = React.useState<GoalCycleDraft>(() => emptyGoalCycle(undefined, ''))
  const [goalErrors, setGoalErrors] = React.useState<Partial<Record<keyof GoalCycleDraft, string>> | null>(null)
  const [goalSpace, setGoalSpace] = React.useState<{ id: string; slug: string } | null>(null)
  /* F-178: the show a one-show Podcast or Vídeos space is born with; refused, only the show is retried. */
  const [showDraft, setShowDraft] = React.useState<ShowDraft>(() => emptyShowDraft(i18n.language))
  const [showErrors, setShowErrors] = React.useState<Record<string, string>>({})
  const [showSpace, setShowSpace] = React.useState<{ id: string; slug: string } | null>(null)
  /* The file a one-file space is born with (F-75): created and sent right after the space. */
  const [downloadDraft, setDownloadDraft] = React.useState<DownloadSingleDraft>(emptyDownloadSingleDraft)
  const [downloadErrors, setDownloadErrors] = React.useState<DownloadSingleErrors | null>(null)
  const [downloadDone, setDownloadDone] = React.useState<DownloadSingleProgress>({})
  const [downloadUpload, setDownloadUpload] = React.useState<UploadProgress | null>(null)
  /* The space exists but its file did not make it: the step stays, with the input, and only the item is retried. */
  const [downloadSpace, setDownloadSpace] = React.useState<{ id: string; slug: string } | null>(null)
  const downloadAbort = React.useRef<AbortController | null>(null)
  /* Slug of the space just made, for landing on it after a retried setup. */
  const createdSlugRef = React.useRef<string | undefined>(undefined)

  /* The error sits above the form, and a long step (the store's product list)
     leaves it out of view right where the create button was clicked. */
  React.useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [error])

  /* The catalog decides how the picker is grouped. Until it answers the picker
     is a skeleton; if it fails the community modules are shown, so creating a
     space never depends on that request. A layout effect, so a catalog another
     picker already cached replaces the skeleton before the first paint. */
  React.useLayoutEffect(() => {
    if (!open) return
    const cached = getCachedModuleCatalog(sdk.getCommunityId())
    if (cached) {
      const next = toRenderableCatalog(cached)
      setCatalog(next)
      setActiveCategory((current) => (next.some((c) => c.key === current) ? current : next[0].key))
      return
    }
    let canceled = false
    loadModuleCatalog(sdk.getCommunityId(), () => sdk.spaces.listModules())
      .then((categories) => {
        if (canceled) return
        const next = toRenderableCatalog(categories)
        setCatalog(next)
        setActiveCategory((current) =>
          next.some((c) => c.key === current) ? current : next[0].key
        )
      })
      .catch(() => {
        if (canceled) return
        setCatalog(FALLBACK_CATALOG)
        setActiveCategory((current) =>
          FALLBACK_CATALOG.some((c) => c.key === current) ? current : FALLBACK_CATALOG[0].key
        )
      })
    return () => {
      canceled = true
    }
  }, [open])

  /**
   * In `single` the space IS the call, so creating one without the other would
   * leave the space half-built — hence an extra step instead of creating
   * straight away.
   */
  /* One item instead of a list: the mode in the module's slice, as the registry says where it lives. */
  const isSingleItem = isSingleItemConfig(selectedModule, selectedModule ? (moduleConfig as Record<string, unknown>)[selectedModule] : undefined)
  const needsCallStep = selectedModule === 'call4papers' && isSingleItem

  const setup = selectedModule ? getModule(selectedModule)?.setup : undefined
  /* F-198: what the catalog says the module offers ("Exibição" stays hidden without it). */
  const layoutOptions = selectedModule ? moduleLayoutsOf(catalog, selectedModule) : null
  const chosenLayout = layoutPick && layoutPick.module === selectedModule ? layoutPick.layout : null

  /* What a store shows has a body of its own (the checklist and its order),
     so it gets the same extra step as a single call (PADROES-UI §9). */
  const needsStorefrontStep = selectedModule === 'storefront'
  /* A one-course space is born with its course, so the course gets a step of
     its own (PADROES-UI §14: the mode is short, the course is longer). */
  const needsCourseStep = selectedModule === 'courses' && isSingleItem
  /* A module whose setup has a body of its own (classifieds terms, downloads
     mode and categories) gets a step after the details (PADROES-UI §14). */
  const needsSetupStep = setup?.wizardStep === 'own'
  /* A one-survey space is born with its survey (title, template, mode), then opens in its editor. */
  const needsSurveyStep = selectedModule === 'surveys' && isSingleItem
  /* A one-form space is born with its form (the title), then opens in its editor (F-109). */
  const needsFormStep = selectedModule === 'forms' && isSingleItem
  /* A one-cycle Performance Review space is born with its cycle (title, period, window), then opens in its editor (F-48). */
  const needsReviewStep = selectedModule === 'performanceReview' && isSingleItem
  /* A one-cycle Metas & OKRs space is born with its cycle (title, period), then opens in it (F-50). */
  const needsGoalStep = selectedModule === 'goalsOkrs' && isSingleItem
  /* A one-show Podcast or Vídeos space is born with its show (name, cover, description…), then opens on it (F-178). */
  const needsShowStep = (selectedModule === 'podcast' || selectedModule === 'videos') && isSingleItem
  const hasThirdStep = needsCallStep || needsStorefrontStep || needsCourseStep || needsSetupStep || needsSurveyStep || needsFormStep || needsReviewStep || needsGoalStep || needsShowStep
  /* A one-file space: after its terms (step 3) comes the file itself, then it opens on it. */
  const needsDownloadStep = selectedModule === 'downloads' && isSingleItem
  /* Once the space exists its terms are saved; the file is all that is left. */
  const spaceExists = Boolean(surveySpace || downloadSpace || formSpace || reviewSpace || goalSpace || showSpace)

  const totalSteps = needsDownloadStep ? 4 : hasThirdStep ? 3 : 2

  React.useEffect(() => {
    if (open) {
      setCurrentStep(1)
      setSelectedModule(null)
      setCommonData({
        title: '',
        description: '',
        slug: '',
        icon: 'file-text',
        visibility: 'members'
      })
      setModuleConfig({})
      setFeedScope(null)
      setFeedWidgets(null)
      setFeedScopeDefault('section')
      setError('')
      setSlugError(null)
      setCourseErrors({})
      setSetupErrors(null)
      setSurveyDraft(emptySurveyDraft())
      setSurveyTitleError(undefined)
      setSurveySpace(null)
      setFormDraft(emptyFormDraft())
      setFormTitleError(undefined)
      setFormSpace(null)
      setReviewDraft(emptyCycleBasics(undefined, ''))
      setReviewErrors(null)
      setReviewSpace(null)
      setGoalDraft(emptyGoalCycle(undefined, ''))
      setGoalErrors(null)
      setGoalSpace(null)
      setShowDraft(emptyShowDraft())
      setShowErrors({})
      setShowSpace(null)
      setDownloadDraft(emptyDownloadSingleDraft())
      setDownloadErrors(null)
      setDownloadDone({})
      setDownloadUpload(null)
      setDownloadSpace(null)
      setIsSubmitting(false)
      setActiveCategory(FALLBACK_CATALOG[0].key)
      setPendingSetup(null)
      setPendingSpace(null)
      setRetryError('')
      setRetrying(false)
    }
  }, [open])

  /* Runs after the reset above: the wizard opens on `initialModule` when one is given. */
  const selectModuleRef = React.useRef<(module: string) => void>(() => {})
  React.useEffect(() => {
    if (open && initialModule) selectModuleRef.current(initialModule)
  }, [open, initialModule])

  const handleModuleSelect = (module: string) => {
    // Announced-only modules have no wizard steps and no server support.
    if (!isReadyModule(module)) return
    setSelectedModule(module)
    /* The suggestions are already in place (PADROES-UI §9: no selector opens empty). */
    const chosenSetup = getModule(module)?.setup
    if (chosenSetup) {
      setModuleConfig((prev) => ({ ...prev, [module]: (prev as Record<string, unknown>)[module] ?? chosenSetup.defaultConfig(t) }))
      const defaultWhoCanPost = chosenSetup.defaultWhoCanPost
      if (defaultWhoCanPost) setCommonData((prev) => ({ ...prev, whoCanPost: prev.whoCanPost ?? defaultWhoCanPost }))
    }
    setCommonData(prev => ({
      ...prev,
      icon: moduleIconName(module)
    }))
    if (module === 'feed') loadFeedScopeDefault()
  }

  /* Starts from the menu's copy of the section and corrects it with a fresh read. */
  const loadFeedScopeDefault = () => {
    const fromSection = (section.spaces ?? []).some((space) => space.module === 'feed' && !space.deletedAt)
    setFeedScopeDefault(fromSection ? 'space' : 'section')
    Promise.resolve(sdk.spaces.getBySection(section.id))
      .then((result: unknown) => {
        const spaces = (Array.isArray(result) ? result : (result as { items?: unknown[] } | null)?.items ?? []) as Array<{ module?: string }>
        setFeedScopeDefault(spaces.some((space) => space.module === 'feed') ? 'space' : 'section')
      })
      .catch(() => { /* keep the menu's answer */ })
  }

  const handleStep1Continue = () => {
    if (!selectedModule) {
      setError(t('common:validation.moduleRequired'))
      return
    }
    setError('')
    setCurrentStep(2)
  }

  const handleBackToStep1 = () => {
    setCurrentStep(1)
    setError('')
  }

  const handleStep2Continue = async () => {
    if (!commonData.title.trim()) {
      setError(t('common:validation.titleRequired'))
      return
    }
    if (selectedModule === 'url' && !(moduleConfig.url?.externalUrl?.trim())) {
      setError(t('common:spaces.externalUrlRequired'))
      return
    }
    setError('')

    /* The cycle's suggestions (this year, a two-week window from today) in the community's time zone. */
    if (needsGoalStep && !goalDraft.title) {
      setGoalDraft(emptyGoalCycle(community?.timezone, t('goals:cycleForm.defaultTitle', { year: new Date().getFullYear() })))
    }
    if (needsReviewStep && !reviewDraft.title) {
      setReviewDraft(emptyCycleBasics(community?.timezone, t('performanceReview:cycleForm.defaultTitle', { year: new Date().getFullYear() })))
    }
    if (needsStorefrontStep || needsSetupStep || needsSurveyStep || needsFormStep || needsReviewStep || needsGoalStep || needsShowStep) {
      setCurrentStep(3)
      return
    }

    if (needsCourseStep) {
      if (!moduleConfig.courses?.course) {
        setModuleConfig((prev) => ({ ...prev, courses: { ...prev.courses, course: emptyCourseDraft() } }))
      }
      setCurrentStep(3)
      return
    }

    // "Single call" mode: go configure the call before creating.
    if (needsCallStep) {
      if (!moduleConfig.call4papers?.call) {
        setModuleConfig((prev) => ({
          ...prev,
          call4papers: { ...prev.call4papers, call: emptyCallDraft() },
        }))
      }
      setCurrentStep(3)
      return
    }

    await handleCreateSpace()
  }

  /**
   * What `POST /spaces` does not do for a call4papers space.
   *
   * The mode (`single`/`multi`) and who opens calls do not live in
   * `Space.moduleConfig`: they live in `Call4PapersSpace`, behind
   * `PUT /call4papers/spaces/:id`. In `single` the call itself is another
   * resource, made right after.
   *
   * `retry` is the second pass on a space that already exists: if the first
   * pass got as far as creating the call, it is updated instead of created
   * again, which the single mode would refuse.
   */
  const finishCall4PapersSpace = async (spaceId: string, retry = false) => {
    const config = moduleConfig.call4papers
    const mode = config?.spaceMode || 'multi'

    await sdk.call4papers.updateSpace(
      spaceId,
      mode === 'multi'
        ? { mode, whoCanOpenCall: config?.whoCanOpenCall || 'staff' }
        : { mode }
    )

    if (mode !== 'single' || !config?.call) return

    const existing = retry ? (await sdk.call4papers.getSpace(spaceId))?.call ?? null : null
    await saveCall({
      id: existing?.id,
      before: existing,
      spaceId,
      draft: config.call,
      callType: config.callType || 'simple'
    })
  }

  /**
   * Every setting saved after the space exists. A failure here leaves the space
   * standing and returns which part is missing, instead of throwing into the
   * generic error that would invite a second "create".
   */
  const finishModuleSetup = async (spaceId: string, retry = false): Promise<PendingSetup | null> => {
    if (selectedModule === 'call4papers') {
      try {
        await finishCall4PapersSpace(spaceId, retry)
      } catch {
        return { spaceId, kind: 'call4papers' }
      }
    }
    /* The API creates the issuing rule as `admins`; only a different choice
       needs its own call (F-42 §4.3). */
    if (selectedModule === 'certificates' && moduleConfig.certificates?.whoCanIssue === 'staff') {
      try {
        await sdk.certificates.updateSpace(spaceId, 'staff')
      } catch {
        return { spaceId, kind: 'certificates' }
      }
    }
    /* Setups saved by the module's own endpoint (classifieds terms, downloads
       mode and categories). */
    if (setup?.persist.type === 'endpoint' && selectedModule) {
      try {
        await setup.persist.save(spaceId, (moduleConfig as Record<string, unknown>)[selectedModule] ?? setup.defaultConfig(t), t)
      } catch {
        return { spaceId, kind: selectedModule as Exclude<SpaceModule, 'storefront'> }
      }
    }
    /* A new store already shows every active product; only a chosen list
       needs saving. F-29 phase 3: the list is products (one-offs and add-ons)
       plus whole plans. */
    const storefront = moduleConfig.storefront
    if (selectedModule === 'storefront' && storefront?.mode === 'selected') {
      const selection: StorefrontConfig = {
        mode: 'selected',
        productIds: storefront.productIds ?? [],
        planIds: storefront.planIds ?? []
      }
      try {
        await sdk.storefront.updateConfig(spaceId, selection)
      } catch {
        return { spaceId, kind: 'storefront', selection }
      }
    }
    return null
  }

  /* Every new space opens once it exists (David, 2026-10-23): a one-item space lands on its item (its own
     page is that item), a list on the list. An external link has no page of its own, so it stays put. */
  const landsOnItem = Boolean(selectedModule) && selectedModule !== 'url'
  const finishCreated = (slug: string | undefined) => {
    setOpen(false)
    onSpaceCreated?.()
    if (landsOnItem && slug) navigate(`/${section.slug}/${slug}`)
  }

  /** `POST /surveys` on the space just made; `false` leaves the step open with the reason. */
  const createSurvey = async (spaceId: string): Promise<boolean> => {
    try {
      const description = surveyDraft.description.replace(/<[^>]*>/g, '').trim() ? surveyDraft.description : null
      await sdk.surveys.create({
        spaceId,
        title: surveyDraft.title.trim(),
        description,
        template: surveyDraft.template,
        anonymity: surveyDraft.anonymity,
        responseMode: surveyDraft.responseMode
      })
      return true
    } catch (caught: unknown) {
      const err = caught as { message?: string; response?: { data?: { message?: string; errors?: Array<{ param?: string; message?: string }> } } } | null
      const onTitle = err?.response?.data?.errors?.find((e) => e.param === 'title')?.message
      if (onTitle) setSurveyTitleError(onTitle)
      const detail = err?.response?.data?.message || err?.message || t('common:errors.generic')
      setError(t('surveys:wizard.surveyFailed', { detail }))
      return false
    }
  }

  /** `POST /media-shows` on the space just made (F-178); `false` leaves the step open with the reason. */
  const createShow = async (spaceId: string): Promise<boolean> => {
    try {
      await createShowFromDraft(spaceId, showDraft, selectedModule === 'videos' ? 'videos' : 'podcast')
      return true
    } catch (caught: unknown) {
      const err = caught as { message?: string; response?: { data?: { message?: string; errors?: Array<{ param?: string; message?: string }> } } } | null
      const onTitle = err?.response?.data?.errors?.find((e) => e.param === 'title')?.message
      if (onTitle) setShowErrors({ title: onTitle })
      const detail = err?.response?.data?.message || err?.message || t('common:errors.generic')
      setError(t('media:wizard.showFailed', { detail }))
      return false
    }
  }

  /** `POST /forms` on the space just made (F-109); `false` leaves the step open with the reason. */
  const createForm = async (spaceId: string): Promise<boolean> => {
    try {
      await sdk.forms.create({
        spaceId,
        title: formDraft.title.trim(),
        description: formDraft.description.replace(/<[^>]*>/g, '').trim() ? formDraft.description : null
      })
      return true
    } catch (caught: unknown) {
      const err = caught as { message?: string; response?: { data?: { message?: string; errors?: Array<{ param?: string; message?: string }> } } } | null
      const onTitle = err?.response?.data?.errors?.find((e) => e.param === 'title')?.message
      if (onTitle) setFormTitleError(onTitle)
      const detail = err?.response?.data?.message || err?.message || t('common:errors.generic')
      setError(t('forms:wizard.formFailed', { detail }))
      return false
    }
  }

  /** `POST /performance-reviews/cycles` on the space just made (F-48); `false` leaves the step open with the reason. */
  const createReviewCycle = async (spaceId: string): Promise<boolean> => {
    try {
      await sdk.performanceReviews.createCycle({ spaceId, ...reviewDraft, title: reviewDraft.title.trim() })
      return true
    } catch (caught: unknown) {
      setReviewErrors(cycleBasicsApiErrors(caught))
      const err = caught as { message?: string; response?: { data?: { message?: string } } } | null
      const detail = err?.response?.data?.message || err?.message || t('common:errors.generic')
      setError(t('performanceReview:wizard.cycleFailed', { detail }))
      return false
    }
  }

  /** `POST /goals/cycles` on the space just made (F-50); `false` leaves the step open with the reason. */
  const createGoalCycle = async (spaceId: string): Promise<boolean> => {
    try {
      await sdk.goals.createCycle({ spaceId, ...goalDraft, title: goalDraft.title.trim() })
      return true
    } catch (caught: unknown) {
      const err = caught as { message?: string; response?: { data?: { message?: string; errors?: Array<{ param?: string; message?: string }> } } } | null
      const hit = err?.response?.data?.errors?.find((e) => e.param === 'title' || e.param === 'startsOn' || e.param === 'endsOn')
      if (hit?.param && hit.message) setGoalErrors({ [hit.param]: hit.message })
      const detail = err?.response?.data?.message || err?.message || t('common:errors.generic')
      setError(t('goals:wizard.cycleFailed', { detail }))
      return false
    }
  }

  /** The item and its file on the space just made; `false` leaves the step open with the reason and the input. */
  const createDownload = async (spaceId: string): Promise<boolean> => {
    downloadAbort.current = new AbortController()
    setDownloadUpload({ percent: 0 })
    let done = downloadDone
    try {
      await createDownloadSingle(spaceId, downloadDraft, done, (next) => { done = next; setDownloadDone(next) }, setDownloadUpload, downloadAbort.current.signal)
      return true
    } catch (caught: unknown) {
      const reason = downloadSingleFailure(caught, t)
      setError(t('downloads:wizard.failed', { detail: reason ?? t('downloads:wizard.canceled') }))
      return false
    } finally {
      setDownloadUpload(null)
    }
  }

  /* Step 3 of a one-file space: the terms are checked, then the file step. */
  const handleDownloadTermsContinue = () => {
    if (!setup || !selectedModule) return
    const invalid = setup.validate((moduleConfig as Record<string, unknown>)[selectedModule] ?? setup.defaultConfig(t), t)
    setSetupErrors(invalid)
    if (invalid) return
    setError('')
    setCurrentStep(4)
  }

  const handleCreateSpace = async () => {
    if (!selectedModule) return
    if (needsDownloadStep) {
      const invalid = validateDownloadSingle(downloadDraft, t, i18n.language)
      setDownloadErrors(invalid)
      if (invalid) return
    }
    /* Second try after a refused file: the space is there, only the item goes. */
    if (needsDownloadStep && downloadSpace) {
      setIsSubmitting(true)
      setError('')
      const ok = await createDownload(downloadSpace.id)
      setIsSubmitting(false)
      if (ok) finishCreated(downloadSpace.slug)
      return
    }
    if (needsShowStep && !showDraft.title.trim()) {
      setShowErrors({ title: t('media:show.errors.titleRequired') })
      return
    }
    /* Second try after a refused show: the space is there, only the show goes. */
    if (needsShowStep && showSpace) {
      setIsSubmitting(true)
      setError('')
      const ok = await createShow(showSpace.id)
      setIsSubmitting(false)
      if (ok) finishCreated(showSpace.slug)
      return
    }
    if (needsGoalStep) {
      const invalid = validateGoalCycle(goalDraft, (key) => t(`goals:${key}`))
      setGoalErrors(invalid)
      if (invalid) return
    }
    /* Second try after a refused cycle: the space is there, only the cycle goes. */
    if (needsGoalStep && goalSpace) {
      setIsSubmitting(true)
      setError('')
      const ok = await createGoalCycle(goalSpace.id)
      setIsSubmitting(false)
      if (ok) finishCreated(goalSpace.slug)
      return
    }
    if (needsReviewStep) {
      const invalid = validateCycleBasics(reviewDraft, (key) => t(`performanceReview:${key}`))
      setReviewErrors(invalid)
      if (invalid) return
    }
    /* Second try after a refused cycle: the space is there, only the cycle goes. */
    if (needsReviewStep && reviewSpace) {
      setIsSubmitting(true)
      setError('')
      const ok = await createReviewCycle(reviewSpace.id)
      setIsSubmitting(false)
      if (ok) finishCreated(reviewSpace.slug)
      return
    }
    if (needsFormStep && !formDraft.title.trim()) {
      setFormTitleError(t('forms:editor.titleRequired'))
      return
    }
    setFormTitleError(undefined)
    /* Second try after a refused form: the space is there, only the form goes. */
    if (needsFormStep && formSpace) {
      setIsSubmitting(true)
      setError('')
      const ok = await createForm(formSpace.id)
      setIsSubmitting(false)
      if (ok) finishCreated(formSpace.slug)
      return
    }
    if (needsSurveyStep && !surveyDraft.title.trim()) {
      setSurveyTitleError(t('surveys:editor.titleRequired'))
      return
    }
    setSurveyTitleError(undefined)
    /* Second try after a refused survey: the space is there, only the survey goes. */
    if (needsSurveyStep && surveySpace) {
      setIsSubmitting(true)
      setError('')
      const ok = await createSurvey(surveySpace.id)
      setIsSubmitting(false)
      if (ok) finishCreated(surveySpace.slug)
      return
    }
    if (
      selectedModule === 'storefront' &&
      moduleConfig.storefront?.mode === 'selected' &&
      !(moduleConfig.storefront.productIds?.length || moduleConfig.storefront.planIds?.length)
    ) {
      setError(t('common:storefront.pickAtLeastOne'))
      return
    }

    /* Checked before anything exists: a refusal after the space is created would leave it half set up. */
    if (setup) {
      const invalid = setup.validate((moduleConfig as Record<string, unknown>)[selectedModule] ?? setup.defaultConfig(t), t)
      setSetupErrors(invalid)
      if (invalid) return
    }

    /* The call of a one-call space is checked before anything exists: refused after, it left a space with no call. */
    const callDraft = needsCallStep ? moduleConfig.call4papers?.call : undefined
    const callProblem = callDraft ? validateCallDraft(callDraft, moduleConfig.call4papers?.callType || 'simple') : null
    if (callProblem) {
      setError(t(`call4papers:${callProblem}`))
      return
    }

    // The course of a one-course space needs a title before anything is created.
    const courseDraft = needsCourseStep ? moduleConfig.courses?.course : undefined
    if (needsCourseStep && !courseDraft?.title.trim()) {
      setCourseErrors({ title: t('courses:form.errors.titleRequired') })
      return
    }
    setCourseErrors({})

    setIsSubmitting(true)
    try {
      // Get existing spaces to calculate order
      /* Responses are native (no `{ data }` envelope) since the official SDK:
         reading `.data` here gave every new space order 0, tied with the
         section's first space. After the highest order, not the count, so a
         gap left by a deleted space does not produce a tie either. */
      const existingSpaces: unknown = await sdk.spaces.getBySection(section.id)
      const siblings = (Array.isArray(existingSpaces)
        ? existingSpaces
        : (existingSpaces as { items?: unknown[] } | null)?.items ?? []) as Array<{ order?: number }>
      const nextOrder = siblings.reduce((max, space) => Math.max(max, (space.order ?? -1) + 1), 0)

      /* `commonData.isPrivate` DOES NOT EXIST — the `CommonSpaceData` type has
         `visibility`. The field was only read, never written, so the comparison
         yielded `undefined`, fell into the last branch, and EVERY space was born
         `public`, whatever was chosen in step 1 of the wizard. (APP-1) */
      const visibility = commonData.visibility

      const created = (await sdk.spaces.create({
        sectionId: section.id,
        title: commonData.title,
        description: commonData.description,
        slug: generateSlug(commonData.slug),
        icon: moduleIconName(selectedModule),
        module: selectedModule,
        order: nextOrder,
        visibility,
        // Who can create inside it (APP-50): collected by the wizard, never sent before.
        canCreate: commonData.whoCanPost || 'anyone',
        externalUrl: selectedModule === 'url' ? moduleConfig.url?.externalUrl : undefined,
        /* Only the chosen module's slice, and only for modules whose setup
           lives in the space: call4papers, certificates and the store keep
           theirs in their own tables, saved right after (`finishModuleSetup`).
           Sending the call4papers slice stored a copy nothing reads, and in
           single mode it carried the whole call draft. */
        moduleConfig: setup
          /* A setup stored in the space travels with it (attendance, F-78 section 6.2); the others are saved right after. */
          ? (setup.persist.type === 'moduleConfig'
            ? setup.persist.toModuleConfig((moduleConfig as Record<string, unknown>)[selectedModule] ?? setup.defaultConfig(t), null)
            : undefined)
          : selectedModule === 'certificates' || selectedModule === 'storefront' || selectedModule === 'call4papers'
            ? undefined
            : selectedModule === 'courses'
              /* Absent means a list. Only the mode goes in the space; the course draft is its own key. */
              ? (needsCourseStep ? { mode: 'single' } : undefined)
              : ((moduleConfig as Record<string, unknown>)[selectedModule] ?? undefined),
        /* Space and course are born together in one transaction: if the course
           is refused, nothing is created. */
        course: courseDraft
          ? {
              title: courseDraft.title.trim(),
              level: courseDraft.level,
              ...(courseDraft.description.replace(/<[^>]*>/g, '').trim() ? { description: courseDraft.description } : {}),
              /* Only when the person turned it on; the text goes as typed, `null` keeps following the default. */
              ...(courseDraft.certificate?.enabled
                ? { certificate: { enabled: true, templateId: courseDraft.certificate.templateId, text: courseDraft.certificate.text?.trim() ? courseDraft.certificate.text : null } }
                : {}),
            }
          : undefined,
        /* Only when it is private: sending an empty set alongside another
           visibility would erase grants nobody asked to erase. */
        access: visibility === 'private' ? commonData.access : undefined,
        /* F-161a: only when the person picked one; the server's default is `inherit`. */
        ...(commonData.lockedDisplay ? { lockedDisplay: commonData.lockedDisplay } : {}),
        /* Only when the person changed it: otherwise the server picks the same default shown. */
        ...(selectedModule === 'feed' && feedScope ? { feedScope } : {}),
        ...(selectedModule === 'feed' && canEditFeedWidgets && feedWidgets ? { feedWidgets } : {}),
        /* F-198: only when the person picked one; untouched, the space is born on the module's default. */
        ...(chosenLayout ? { layout: chosenLayout } : {}),
      } as Parameters<typeof sdk.spaces.create>[0])) as { id?: string; data?: { id?: string } } | null

      const spaceId = created?.id ?? created?.data?.id
      const createdSlug = (created as { slug?: string } | null)?.slug ?? (created?.data as { slug?: string } | undefined)?.slug ?? generateSlug(commonData.slug)
      createdSlugRef.current = createdSlug
      if (spaceId) {
        const pending = await finishModuleSetup(spaceId)
        if (pending) {
          /* `onSpaceCreated` refreshes the menu, and the menu unmounts this
             dialog while it reloads: it waits until the notice is dismissed. */
          setPendingSetup(pending)
          return
        }
        if (needsDownloadStep && !(await createDownload(spaceId))) {
          setDownloadSpace({ id: spaceId, slug: createdSlug })
          return
        }
        if (needsSurveyStep && !(await createSurvey(spaceId))) {
          /* The menu is not refreshed yet for the same reason: it waits until the survey goes or the dialog closes. */
          setSurveySpace({ id: spaceId, slug: createdSlug })
          return
        }
        if (needsFormStep && !(await createForm(spaceId))) {
          setFormSpace({ id: spaceId, slug: createdSlug })
          return
        }
        if (needsReviewStep && !(await createReviewCycle(spaceId))) {
          setReviewSpace({ id: spaceId, slug: createdSlug })
          return
        }
        if (needsShowStep && !(await createShow(spaceId))) {
          setShowSpace({ id: spaceId, slug: createdSlug })
          return
        }
        if (needsGoalStep && !(await createGoalCycle(spaceId))) {
          setGoalSpace({ id: spaceId, slug: createdSlug })
          return
        }
      }

      finishCreated(createdSlug)
    } catch (caught: unknown) {
      const err = caught as { message?: string; response?: { data?: { message?: string; errors?: Array<{ param?: string; message?: string }> } } } | null
      /* Only `POST /spaces` (and the order lookup before it) can land here:
         whatever runs after the space exists is caught in `finishModuleSetup`. */
      const apiErrors: Array<{ param?: string; message?: string }> = err?.response?.data?.errors ?? []
      const onCourse = apiErrors.filter((e) => e.param === 'course.title' || e.param === 'course.level' || /^(course\.)?certificate\.(templateId|text)$/.test(e.param ?? ''))
      if (onCourse.length > 0) {
        setCourseErrors({
          title: onCourse.find((e) => e.param === 'course.title')?.message,
          level: onCourse.find((e) => e.param === 'course.level')?.message,
          templateId: onCourse.find((e) => e.param?.endsWith('certificate.templateId'))?.message,
          text: onCourse.find((e) => e.param?.endsWith('certificate.text'))?.message,
        })
        setCurrentStep(3)
        return
      }
      const onSetup = setup?.errorsFromApi?.(err)
      if (onSetup) {
        setSetupErrors(onSetup)
        setCurrentStep(setup?.wizardStep === 'own' ? 3 : 2)
        return
      }
      const onSlug = slugErrorFrom(err)
      if (onSlug) {
        /* The slug is on the details step: go back to it, with the reason under the field. */
        setSlugError(onSlug)
        setCurrentStep(2)
        return
      }
      const detail = err?.response?.data?.message || err?.message
      /* F-34: the module's extension is not in the plan (403 on `module`): back to the
         picker, with the API's translated reason above it. */
      if (apiErrors.some((e) => e.param === 'module')) setCurrentStep(1)
      setError(detail || t('common:errors.generic'))
    } finally {
      setIsSubmitting(false)
    }
  }

  /* Leaving the "setup not saved" notice, by any way out: the space exists,
     so the menu must learn about it now. */
  const finishPendingSetup = () => {
    setStorefrontPickerOpen(false)
    setPendingSpace(null)
    setOpen(false)
    onSpaceCreated?.()
  }

  const handleOpenChange = (next: boolean) => {
    if (!next && pendingSetup) {
      finishPendingSetup()
      return
    }
    /* Closing with the survey (or the file) still refused: the space exists, so the menu must learn about it. */
    if (!next && spaceExists) {
      downloadAbort.current?.abort()
      setOpen(false)
      onSpaceCreated?.()
      return
    }
    setOpen(next)
  }

  /* Same request, on the space that already exists. */
  const retryPendingSetup = async () => {
    if (!pendingSetup) return
    setRetrying(true)
    setRetryError('')
    const stillPending = await finishModuleSetup(pendingSetup.spaceId, true)
    setRetrying(false)
    if (stillPending) {
      setPendingSetup(stillPending)
      setRetryError(t('common:spaces.setupRetryFailed'))
      return
    }
    /* The terms are saved now: back to the file, which is still in the step. */
    if (needsDownloadStep && createdSlugRef.current) {
      setDownloadSpace({ id: pendingSetup.spaceId, slug: createdSlugRef.current })
      setPendingSetup(null)
      setCurrentStep(4)
      return
    }
    finishPendingSetup()
    if (landsOnItem && createdSlugRef.current) navigate(`/${section.slug}/${createdSlugRef.current}`)
  }

  /* The store has its own settings dialog; the rest are in the space's. */
  const openPendingSettings = async () => {
    if (!pendingSetup) return
    if (pendingSetup.kind === 'storefront') {
      setStorefrontPickerOpen(true)
      return
    }
    setRetryError('')
    try {
      const fresh = (await sdk.spaces.get(pendingSetup.spaceId)) as Space | { data?: Space }
      setPendingSpace(('data' in fresh && fresh.data ? fresh.data : fresh) as Space)
    } catch {
      setRetryError(t('common:spaces.setupOpenFailed'))
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
  }

  const canContinueStep1 = selectedModule !== null

  /**
   * The module's own questions on the details step (PADROES-UI §14).
   *
   * Only call4papers and the external link ask anything here. Certificates
   * ask "who issues" in the "who can post" slot, the store has a step of its
   * own, and the other modules ask nothing: their old controls wrote keys that
   * nothing reads, and F-64 phase 1 took them out.
   */
  const renderModuleFields = () => {
    switch (selectedModule) {
      case 'call4papers':
        return (
          <Call4PapersModuleFields
            config={moduleConfig.call4papers || {}}
            onChange={(config) => setModuleConfig({ ...moduleConfig, call4papers: config })}
            disabled={isSubmitting}
          />
        )
      case 'courses':
        return (
          <CoursesModuleFields
            config={moduleConfig.courses || {}}
            onChange={(config) => setModuleConfig({ ...moduleConfig, courses: config })}
            disabled={isSubmitting}
          />
        )
      case 'feed':
        return (
          <FeedModuleFields
            value={feedScope ?? feedScopeDefault}
            onChange={setFeedScope}
            disabled={isSubmitting}
            {...(canEditFeedWidgets ? { widgets: feedWidgets ?? DEFAULT_FEED_WIDGETS, onWidgetsChange: setFeedWidgets } : {})}
          />
        )
      case 'url':
        return (
          <UrlModuleFields
            config={moduleConfig.url || { externalUrl: '' }}
            onChange={(config) => setModuleConfig({ ...moduleConfig, url: config })}
            disabled={isSubmitting}
          />
        )
      default:
        return null
    }
  }

  selectModuleRef.current = handleModuleSelect

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {triggerElement ? triggerElement : trigger === 'button' ? (
          <button
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-400 rounded-lg transition-colors hover:bg-gray-50 hover:text-gray-500"
            aria-label={t('navigation:sidebar.addSpace')}
          >
            <Plus className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">{t('navigation:sidebar.addSpace')}</span>
          </button>
        ) : (
          <button
            className="flex items-center gap-1.5 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
            title={t('common:spaces.createSpace')}
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        )}
      </DialogTrigger>

      <DialogContent guard className="max-h-[85dvh] w-[calc(100%-1.5rem)] max-w-5xl p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle>{t('common:spaces.createSpace')}</DialogTitle>
          {/* `asChild`: the description renders a <p>, and a <div> inside it is
              invalid nesting that React reports on every open. */}
          <DialogDescription asChild>
            <div className="flex items-center gap-2 text-xs text-gray-500 mt-2">
              <span className={currentStep === 1 ? 'text-primary font-medium' : ''}>
                {t('common:spaces.stepModule')}
              </span>
              <span>/</span>
              <span className={currentStep === 2 ? 'text-primary font-medium' : ''}>
                {t('common:spaces.stepConfigure')}
              </span>
              {totalSteps >= 3 && (
                <>
                  <span>/</span>
                  <span className={currentStep === 3 ? 'text-primary font-medium' : ''}>
                    {needsStorefrontStep ? t('common:spaces.stepStorefront') : needsCourseStep ? t('common:spaces.stepCourse') : needsSurveyStep ? t('surveys:wizard.step') : needsFormStep ? t('forms:wizard.step') : needsReviewStep ? t('performanceReview:wizard.step') : needsGoalStep ? t('goals:wizard.step') : needsShowStep ? t('media:wizard.step') : needsSetupStep && setup?.stepLabelKey ? t(setup.stepLabelKey) : t('common:spaces.stepCall')}
                  </span>
                </>
              )}
              {totalSteps === 4 && (
                <>
                  <span>/</span>
                  <span className={currentStep === 4 ? 'text-primary font-medium' : ''}>{t('downloads:wizard.step')}</span>
                </>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        {pendingSetup ? (
          <div className="min-w-0 space-y-4" data-testid="setup-save-failed" data-kind={pendingSetup.kind}>
            <div role="alert" className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
              <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
              <div className="space-y-1">
                <p className="text-sm font-semibold text-gray-900">
                  {pendingSetup.kind === 'storefront'
                    ? t('common:storefront.createSaveFailedTitle')
                    : t(`common:spaces.setupFailed.${pendingSetup.kind}Title`)}
                </p>
                <p className="text-sm text-gray-700">
                  {pendingSetup.kind === 'storefront'
                    ? t('common:storefront.createSaveFailed')
                    : pendingSetup.kind === 'call4papers' && (moduleConfig.call4papers?.spaceMode || 'multi') === 'single'
                      ? t('common:spaces.setupFailed.call4papersSingle')
                      : t(`common:spaces.setupFailed.${pendingSetup.kind}`)}
                </p>
              </div>
            </div>
            {retryError && (
              <div role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-600">
                {retryError}
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={finishPendingSetup} disabled={retrying}>
                {t('common:close')}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full sm:w-auto"
                onClick={() => void openPendingSettings()}
                disabled={retrying}
                data-testid="setup-open-settings"
              >
                {pendingSetup.kind === 'storefront' ? t('common:storefront.manage') : t('common:spaces.setupOpenSettings')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                className="w-full sm:w-auto"
                onClick={() => void retryPendingSetup()}
                disabled={retrying}
                data-testid="setup-retry"
              >
                {retrying ? t('common:spaces.setupRetrying') : t('common:retry')}
              </Button>
            </DialogFooter>
            {/* Nested, not siblings: the whole sidebar (and this dialog with it)
                unmounts while the menu reloads, so these live here until they
                are done. Canceling one comes back to this notice. */}
            {pendingSetup.kind === 'storefront' && (
              <StorefrontSettingsDialog
                spaceId={pendingSetup.spaceId}
                open={storefrontPickerOpen}
                onOpenChange={setStorefrontPickerOpen}
                onSaved={finishPendingSetup}
                initialSelection={pendingSetup.selection}
              />
            )}
            {pendingSpace && (
              <EditSpaceDialog
                space={pendingSpace}
                open
                onOpenChange={(next) => { if (!next) setPendingSpace(null) }}
                onSpaceUpdated={finishPendingSetup}
              />
            )}
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div ref={errorRef} role="alert" className="p-3 text-sm text-red-600 bg-red-50 rounded-lg">
              {error}
            </div>
          )}

          {currentStep === 1 && !catalog && (
            <div className="min-w-0" aria-busy="true" data-module-picker-loading>
              <div className="mb-4 flex flex-wrap gap-2">
                {[96, 80, 112].map((width) => (
                  <Skeleton key={width} className="h-9" width={width} />
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-28 w-full rounded-xl" />
                ))}
              </div>
            </div>
          )}

          {currentStep === 1 && catalog && (
            <Tabs value={activeCategory} onValueChange={setActiveCategory} className="min-w-0">
              {/* Underline tabs (PADROES-UI section 1): on a phone-width modal the row
                  scrolls sideways inside itself, with a shadow on the side that has more. */}
              {catalog.length > 1 && (
                <TabsList size="sm" className="mb-4" aria-label={t('navigation:sidebar.addSpace')}>
                  {catalog.map((category) => (
                    <TabsTrigger key={category.key} value={category.key}>
                      {category.locked && <Lock className="h-3.5 w-3.5" aria-label={t('entitlements:picker.locked')} />}
                      {t(`common:modules.categories.${category.key}`)}
                    </TabsTrigger>
                  ))}
                </TabsList>
              )}
              {catalog.map((category) => (
                <TabsContent key={category.key} value={category.key} className="mt-0">
                  {category.locked && category.modules.some((m) => m.available && isReadyModule(m.key)) && (
                    <p className="mb-3 flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800" data-testid="category-locked">
                      <Lock className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
                      {t('entitlements:picker.lockedCategory')}
                    </p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {category.modules.map(({ key: moduleKey, available, locked }) => {
                      const built = available && isReadyModule(moduleKey)
                      /* F-34: a built module of an extension the plan lacks shows the lock;
                         one still to come keeps "coming soon" (there is nothing to hire yet). */
                      const lockedByPlan = built && locked === true
                      const creatable = built && !lockedByPlan
                      return (
                        <ModuleCard
                          key={moduleKey}
                          icon={pickerIcon(moduleKey)}
                          title={t(`common:modules.${moduleKey}`, { defaultValue: humanizeKey(moduleKey) })}
                          description={t(`common:modules.descriptions.${moduleKey}`, { defaultValue: '' })}
                          isSelected={selectedModule === moduleKey}
                          onClick={() => handleModuleSelect(moduleKey)}
                          disabled={isSubmitting}
                          unavailable={!creatable}
                          badge={
                            lockedByPlan ? (
                              <Badge className="inline-flex items-center gap-1 bg-amber-50 px-2 text-[11px] text-amber-800" data-testid="module-locked">
                                <Lock className="h-3 w-3" aria-hidden="true" />
                                {t('entitlements:picker.locked')}
                              </Badge>
                            ) : !creatable ? (
                              <Badge className="bg-white px-2 text-[11px] text-gray-500">
                                {t('common:modules.comingSoon')}
                              </Badge>
                            ) : null
                          }
                        />
                      )
                    })}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          )}

          {currentStep === 2 && selectedModule && (
            <div className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                  {t('common:spaces.spaceDetails')}
                </h3>
                <CommonSpaceFields
                  data={commonData}
                  onChange={(next) => { setCommonData(next); if (next.slug !== commonData.slug) setSlugError(null) }}
                  slugError={slugError}
                  disabled={isSubmitting}
                  sectionVisibility={section.visibility}
                  sectionTitle={section.title}
                  sectionAdminOnly={section.adminOnly === true}
                  showWhoCanPost={selectedModule !== 'certificates' && hasPosting(selectedModule)}
                  whoCanPostSlot={selectedModule === 'certificates' ? (
                    <CertificatesModuleFields
                      config={moduleConfig.certificates || {}}
                      onChange={(config) => setModuleConfig({ ...moduleConfig, certificates: config })}
                      disabled={isSubmitting}
                    />
                  ) : undefined}
                />
                {layoutOptions && (
                  <SpaceLayoutField
                    options={offeredLayouts(selectedModule, layoutOptions.options)}
                    value={chosenLayout}
                    defaultLayout={layoutOptions.default}
                    onChange={(value) => setLayoutPick({ module: selectedModule, layout: value })}
                    disabled={isSubmitting}
                  />
                )}
              </div>

              {setup && setup.wizardStep === 'details' && selectedModule && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                    {t('common:spaces.moduleConfiguration')}
                  </h3>
                  <setup.Fields
                    config={(moduleConfig as Record<string, unknown>)[selectedModule] ?? setup.defaultConfig(t)}
                    onChange={(config) => { setModuleConfig((prev) => ({ ...prev, [selectedModule]: config })); setSetupErrors(null) }}
                    errors={setupErrors}
                    disabled={isSubmitting}
                  />
                </div>
              )}

              {(selectedModule === 'call4papers' || selectedModule === 'url' || selectedModule === 'courses' || selectedModule === 'feed') && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                    {t('common:spaces.moduleConfiguration')}
                  </h3>
                  {renderModuleFields()}
                </div>
              )}
            </div>
          )}

          {currentStep === 3 && moduleConfig.call4papers?.call && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-900">
                {t('common:spaces.stepCall')}
              </h3>
              <CallSetupForm
                callType={moduleConfig.call4papers?.callType || 'simple'}
                data={moduleConfig.call4papers.call}
                onChange={(call) =>
                  setModuleConfig((prev) => ({
                    ...prev,
                    call4papers: { ...prev.call4papers, call },
                  }))
                }
                disabled={isSubmitting}
              />
            </div>
          )}

          {currentStep === 3 && needsCourseStep && moduleConfig.courses?.course && (
            <CoursesSingleStep
              draft={moduleConfig.courses.course}
              onChange={(course) => {
                setModuleConfig((prev) => ({ ...prev, courses: { ...prev.courses, course } }))
                setCourseErrors({})
              }}
              titleError={courseErrors.title}
              levelError={courseErrors.level}
              certificateErrors={{ templateId: courseErrors.templateId, text: courseErrors.text }}
              disabled={isSubmitting}
            />
          )}

          {currentStep === 3 && needsSurveyStep && (
            <SurveySingleStep
              draft={surveyDraft}
              onChange={(next) => { setSurveyDraft(next); setSurveyTitleError(undefined) }}
              titleError={surveyTitleError}
              disabled={isSubmitting}
            />
          )}

          {currentStep === 3 && needsFormStep && (
            <FormSingleStep
              draft={formDraft}
              onChange={(next) => { setFormDraft(next); setFormTitleError(undefined) }}
              titleError={formTitleError}
              disabled={isSubmitting}
            />
          )}

          {currentStep === 3 && needsReviewStep && (
            <CycleSingleStep
              draft={reviewDraft}
              onChange={(next) => { setReviewDraft(next); setReviewErrors(null) }}
              errors={reviewErrors}
              disabled={isSubmitting}
            />
          )}

          {currentStep === 3 && needsShowStep && (selectedModule === 'podcast' || selectedModule === 'videos') && (
            <div className="min-w-0 space-y-4" data-testid="show-single-step">
              <div className="space-y-1">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-900">{t('media:wizard.step')}</h3>
                <p className="text-xs text-gray-500">{t(`media:wizard.stepHint.${selectedModule}`)}</p>
              </div>
              <ShowFields module={selectedModule} draft={showDraft} onChange={(next) => { setShowDraft(next); setShowErrors({}) }}
                errors={showErrors} disabled={isSubmitting} idPrefix="single-show" />
            </div>
          )}

          {currentStep === 3 && needsGoalStep && (
            <GoalCycleSingleStep
              draft={goalDraft}
              onChange={(next) => { setGoalDraft(next); setGoalErrors(null) }}
              errors={goalErrors}
              disabled={isSubmitting}
            />
          )}

          {currentStep === 3 && needsStorefrontStep && (
            <div className="min-w-0 space-y-4">
              <div className="space-y-1">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-900">
                  {t('common:spaces.stepStorefront')}
                </h3>
                <p className="text-xs text-gray-500">{t('common:storefront.createHint')}</p>
              </div>
              <StorefrontModuleFields
                config={moduleConfig.storefront || {}}
                onChange={(config) => {
                  setModuleConfig((prev) => ({ ...prev, storefront: config }))
                  setError('')
                }}
                disabled={isSubmitting}
              />
            </div>
          )}

          {currentStep === 3 && needsSetupStep && setup && selectedModule && (
            <div className="min-w-0 space-y-4">
              <div className="space-y-1">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-900">
                  {setup.stepLabelKey && t(setup.stepLabelKey)}
                </h3>
                {setup.stepHintKey && <p className="text-xs text-gray-500">{t(setup.stepHintKey)}</p>}
              </div>
              <setup.Fields
                config={(moduleConfig as Record<string, unknown>)[selectedModule] ?? setup.defaultConfig(t)}
                onChange={(config) => {
                  setModuleConfig((prev) => ({ ...prev, [selectedModule]: config }))
                  setSetupErrors(null)
                  setError('')
                }}
                errors={setupErrors}
                disabled={isSubmitting}
              />
            </div>
          )}

          {currentStep === 4 && needsDownloadStep && (
            <DownloadSingleStep
              draft={downloadDraft}
              onChange={(next) => { setDownloadDraft(next); setDownloadErrors(null); setError('') }}
              errors={downloadErrors}
              upload={downloadUpload}
              uploaded={Boolean(downloadDone.uploaded)}
              disabled={isSubmitting}
            />
          )}

          <DialogFooter className="gap-2">
            {currentStep === 1 ? (
              <>
                <Button data-dialog-cancel
                  type="button"
                  variant="outline"
                  onClick={() => setOpen(false)}
                  disabled={isSubmitting}
                >
                  {t('common:actions.cancel')}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleStep1Continue}
                  disabled={isSubmitting || !canContinueStep1}
                >
                  {t('common:next')}
                </Button>
              </>
            ) : currentStep === 2 ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBackToStep1}
                  disabled={isSubmitting}
                >
                  {t('common:previous')}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleStep2Continue}
                  disabled={isSubmitting}
                >
                  {hasThirdStep
                    ? t('common:next')
                    : isSubmitting
                      ? t('common:spaces.creatingSpace')
                      : t('common:spaces.createSpace')}
                </Button>
              </>
            ) : currentStep === 3 && needsDownloadStep ? (
              <>
                <Button type="button" variant="outline" onClick={() => { setCurrentStep(2); setError('') }} disabled={isSubmitting}>
                  {t('common:previous')}
                </Button>
                <Button type="button" variant="secondary" onClick={handleDownloadTermsContinue} disabled={isSubmitting} data-testid="create-space-next">
                  {t('common:next')}
                </Button>
              </>
            ) : (
              <>
                {/* Once the space exists the details step has nothing left to change. */}
                {spaceExists ? (
                  <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={isSubmitting}>
                    {t('common:close')}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setCurrentStep(currentStep === 4 ? 3 : 2); setError('') }}
                    disabled={isSubmitting}
                  >
                    {t('common:previous')}
                  </Button>
                )}
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleCreateSpace}
                  disabled={isSubmitting}
                  data-testid="create-space-submit"
                >
                  {downloadSpace
                    ? (isSubmitting ? t('downloads:wizard.sending') : t('downloads:wizard.send'))
                    : surveySpace
                    ? (isSubmitting ? t('surveys:wizard.creatingSurvey') : t('surveys:wizard.createSurvey'))
                    : formSpace
                    ? (isSubmitting ? t('forms:wizard.creatingForm') : t('forms:wizard.createForm'))
                    : reviewSpace
                    ? (isSubmitting ? t('performanceReview:wizard.creatingCycle') : t('performanceReview:wizard.createCycle'))
                    : goalSpace
                    ? (isSubmitting ? t('goals:wizard.creatingCycle') : t('goals:wizard.createCycle'))
                    : showSpace
                    ? (isSubmitting ? t('media:wizard.creatingShow') : t('media:wizard.createShow'))
                    : isSubmitting ? t('common:spaces.creatingSpace') : t('common:spaces.createSpace')}
                </Button>
              </>
            )}
          </DialogFooter>
        </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
